/**
 * Capital Projects, as the chief executive reads them — not the gates and
 * hold points (the projects screens hold those) but the portfolio: what is
 * committed, what is spent against progress, what has slipped, what is
 * stopped, and what the executive should do about each: press on, hold,
 * rebaseline, or watch.
 *
 * Pure. The page gathers, the desk decides.
 */
import type { SourceRef } from "./decision";
import type { CapexLine, Finance } from "./finance";
import { money } from "./intelligence";
import type { HoldPointReport, ProjectRow } from "./projects";
import { t, tn } from "../i18n";

export type Verdict = "press_on" | "hold" | "rebaseline" | "watch" | "complete";

export interface ProjectView {
  id: string;
  code: string;
  name: string;
  status: string;
  budget: number | null;
  committed: number | null;
  spent: number | null;
  /** spent over budget */
  spentPct: number | null;
  /** how far through the schedule the calendar is */
  elapsedPct: number | null;
  slipDays: number;
  workStopped: boolean;
  holding: number;
  gates: string;
  /** spend running ahead of the calendar, in points */
  burnAhead: number | null;
  verdict: Verdict;
  why: string;
  sources: SourceRef[];
}

export interface CapexInputs {
  now: Date;
  siteCode: string;
  currency: string;
  projects: ProjectRow[];
  holdPoints: HoldPointReport | null;
  finance: Finance | null;
}

export interface CapexDesk {
  generatedAt: string;
  currency: string;
  tiles: {
    key: string;
    label: string;
    value: number | null;
    unit: string;
    text: string;
    tone?: "good" | "bad" | "warn";
    assumed?: boolean;
    sources: SourceRef[];
  }[];
  projects: ProjectView[];
  /** the finance system's capital lines, when the platform's projects do not carry money */
  financeLines: (CapexLine & { pctSpent: number; burnAhead: number })[];
  boardLine: string[];
  gaps: string[];
  figures: Set<string>;
}

const DAY = 86_400_000;
const pct = (a: number, b: number) => (b > 0 ? Math.round((1000 * a) / b) / 10 : null);

export const VERDICT_LABEL: Record<Verdict, string> = {
  press_on: "press on",
  hold: "hold — work is stopped",
  rebaseline: "rebaseline",
  watch: "watch",
  complete: "complete",
};

function view(p: ProjectRow, now: Date): ProjectView {
  const m = p.money;
  const budget = m.budgetMinor != null ? m.budgetMinor / 100 : null;
  const committed = m.committedMinor != null ? m.committedMinor / 100 : null;
  const spent = m.spentMinor != null ? m.spentMinor / 100 : null;
  const spentPct = budget != null && spent != null ? pct(spent, budget) : null;
  const sch = p.schedule;
  const start = sch.startDate ?? sch.baselineStart;
  const end = sch.endDate ?? sch.baselineEnd;
  const elapsedPct =
    start && end
      ? Math.max(
          0,
          Math.min(
            100,
            Math.round(
              (1000 * (now.getTime() - new Date(start).getTime())) /
                Math.max(DAY, new Date(end).getTime() - new Date(start).getTime()),
            ) / 10,
          ),
        )
      : null;
  const slipDays = sch.endSlipDays ?? 0;
  const burnAhead = spentPct != null && elapsedPct != null ? Math.round((spentPct - elapsedPct) * 10) / 10 : null;
  let verdict: Verdict;
  let why: string;
  if (p.status === "complete" || p.status === "cancelled") {
    verdict = "complete";
    why = t("delivered");
  } else if (p.workStopped) {
    verdict = "hold";
    why = tn(
      p.holding,
      "{n} hold point unreleased — nothing moves until they are",
      "{n} hold points unreleased — nothing moves until they are",
    );
  } else if (slipDays > 30 && (burnAhead ?? 0) > 10) {
    verdict = "rebaseline";
    why = t(
      "{days} days late and spend {points} points ahead of the calendar: the baseline no longer describes the job",
      { days: slipDays, points: String(burnAhead) },
    );
  } else if (slipDays > 30) {
    verdict = "rebaseline";
    why = sch.why
      ? t("{days} days late against the baseline: {why}", { days: slipDays, why: sch.why })
      : t("{days} days late against the baseline", { days: slipDays });
  } else if ((burnAhead ?? 0) > 15) {
    verdict = "watch";
    why = t("spend {points} points ahead of the calendar — the money runs out before the work does", {
      points: String(burnAhead),
    });
  } else if (slipDays > 0 || (burnAhead ?? 0) > 5) {
    verdict = "watch";
    why =
      slipDays > 0
        ? t("{days} days late, within tolerance", { days: slipDays })
        : t("spend {points} points ahead of the calendar", { points: String(burnAhead) });
  } else {
    verdict = "press_on";
    why =
      sch.state === "no_baseline" ? t("no baseline to measure against — set one") : t("on its baseline and its budget");
  }
  return {
    id: p.id,
    code: p.code,
    name: p.name,
    status: p.status,
    budget,
    committed,
    spent,
    spentPct,
    elapsedPct,
    slipDays,
    workStopped: p.workStopped,
    holding: p.holding,
    gates: t("{approved} of {total} gates approved", { approved: p.gatesApproved, total: p.gates }),
    burnAhead,
    verdict,
    why,
    sources: [{ kind: "project", ref: p.code, label: p.name, href: `/capital-projects/${p.id}` }],
  };
}

export function buildCapexDesk(inp: CapexInputs): CapexDesk {
  const cur = inp.currency;
  const projects = inp.projects
    .map((p) => view(p, inp.now))
    .sort(
      (a, b) =>
        ["hold", "rebaseline", "watch", "press_on", "complete"].indexOf(a.verdict) -
          ["hold", "rebaseline", "watch", "press_on", "complete"].indexOf(b.verdict) ||
        (b.budget ?? 0) - (a.budget ?? 0),
    );
  const live = projects.filter((p) => p.verdict !== "complete");
  const budget = live.reduce((t, p) => t + (p.budget ?? 0), 0);
  const committed = live.reduce((t, p) => t + (p.committed ?? 0), 0);
  const spent = live.reduce((t, p) => t + (p.spent ?? 0), 0);
  const unpriced = live.filter((p) => p.budget == null);
  const stopped = live.filter((p) => p.workStopped);
  const slipped = live.filter((p) => p.slipDays > 0);
  const financeLines = (inp.finance?.capex ?? []).map((c) => ({
    ...c,
    pctSpent: pct(c.spent, c.budget) ?? 0,
    burnAhead: Math.round(((pct(c.spent, c.budget) ?? 0) - c.pctComplete) * 10) / 10,
  }));
  const finRef: SourceRef[] = inp.finance
    ? [
        inp.finance.basis === "demo"
          ? { kind: "demo", ref: "demo finance feed", label: t("capital lines — demonstration figures") }
          : { kind: "finance", ref: "finance", label: t("capital lines") },
      ]
    : [];
  const finBudget = financeLines.reduce((t, c) => t + c.budget, 0);
  const finSpent = financeLines.reduce((t, c) => t + c.spent, 0);
  const tiles: CapexDesk["tiles"] = [
    {
      key: "live",
      label: t("live projects"),
      value: live.length,
      unit: "",
      text: t("{stopped} stopped at a hold point, {slipped} behind baseline, {unpriced} with no money declared", {
        stopped: stopped.length,
        slipped: slipped.length,
        unpriced: unpriced.length,
      }),
      tone: stopped.length ? "bad" : slipped.length ? "warn" : "good",
      sources: live.slice(0, 6).flatMap((p) => p.sources),
    },
    {
      key: "budget",
      label: t("budget on the platform"),
      value: budget,
      unit: cur,
      text: t("{committed} committed, {spent} spent — {pct}% of budget", {
        committed: money(committed, cur),
        spent: money(spent, cur),
        pct: pct(spent, budget) ?? "—",
      }),
      sources: live.filter((p) => p.budget != null).flatMap((p) => p.sources),
    },
    {
      key: "finance",
      label: t("capital in the finance lines"),
      value: finBudget || null,
      unit: cur,
      text: financeLines.length
        ? t("{spent} spent on {n} lines — {pct}%", {
            spent: money(finSpent, cur),
            n: financeLines.length,
            pct: pct(finSpent, finBudget) ?? "—",
          })
        : t("no capital lines entered"),
      assumed: inp.finance?.basis === "demo",
      sources: finRef,
    },
    {
      key: "hold",
      label: t("stopped by a hold point"),
      value: stopped.length,
      unit: "",
      text: stopped.map((p) => p.name).join("; ") || t("none"),
      tone: stopped.length ? "bad" : "good",
      sources: stopped.flatMap((p) => p.sources),
    },
    {
      key: "slip",
      label: t("days late, worst"),
      value: slipped.length ? Math.max(...slipped.map((p) => p.slipDays)) : 0,
      unit: t("days"),
      text: slipped.length ? slipped.sort((a, b) => b.slipDays - a.slipDays)[0].name : t("nothing behind its baseline"),
      tone: slipped.some((p) => p.slipDays > 30) ? "bad" : slipped.length ? "warn" : "good",
      sources: slipped.flatMap((p) => p.sources),
    },
    {
      key: "burn",
      label: t("spend ahead of calendar, worst"),
      value: live.length ? Math.max(...live.map((p) => p.burnAhead ?? 0)) : null,
      unit: "pts",
      text: live.length
        ? (live.sort((a, b) => (b.burnAhead ?? 0) - (a.burnAhead ?? 0))[0]?.name ?? "")
        : t("no live project"),
      tone: live.some((p) => (p.burnAhead ?? 0) > 15) ? "warn" : "good",
      sources: [],
    },
  ];
  const first =
    projects.find((p) => p.verdict === "hold") ??
    projects.find((p) => p.verdict === "rebaseline") ??
    projects.find((p) => p.verdict === "watch");
  const boardLine = [
    financeLines.length
      ? t(
          "{n} live capital projects at {site}: {budget} budgeted on the platform, {spent} spent; the finance lines carry {finance}.",
          {
            n: live.length,
            site: inp.siteCode,
            budget: money(budget, cur),
            spent: money(spent, cur),
            finance: money(finBudget, cur),
          },
        )
      : t("{n} live capital projects at {site}: {budget} budgeted on the platform, {spent} spent.", {
          n: live.length,
          site: inp.siteCode,
          budget: money(budget, cur),
          spent: money(spent, cur),
        }),
    first
      ? t("First on the desk: {name} — {verdict}: {why}.", {
          name: first.name,
          verdict: t(VERDICT_LABEL[first.verdict]),
          why: first.why,
        })
      : t("Every live project is on its baseline and its budget."),
    unpriced.length
      ? unpriced.length === 1
        ? t(
            "{n} project carries no money on the platform: {names} — the portfolio total is understated by that much.",
            { n: unpriced.length, names: unpriced.map((p) => p.name).join("; ") },
          )
        : t("{n} projects carry no money on the platform: {names} — the portfolio total is understated by that much.", {
            n: unpriced.length,
            names: unpriced.map((p) => p.name).join("; "),
          })
      : t("Every live project carries its money on the platform."),
  ];
  const material = materialForCapex({ tiles, projects, financeLines, boardLine, currency: cur } as CapexDesk);
  const figures = new Set<string>();
  for (const n of material.match(/\d[\d,]*(?:\.\d+)?/g) ?? []) {
    figures.add(n);
    figures.add(n.replace(/,/g, ""));
  }
  for (let n = 0; n <= 12; n++) figures.add(String(n));
  return {
    generatedAt: inp.now.toISOString(),
    currency: cur,
    tiles,
    projects,
    financeLines,
    boardLine,
    gaps: [
      ...(unpriced.length ? [t("{n} projects carry no money on the platform", { n: unpriced.length })] : []),
      ...(inp.finance ? [] : [t("no finance capital lines")]),
      ...(inp.holdPoints ? [] : [t("hold points not available")]),
    ],
    figures,
  };
}

export const CAPEX_MODEL_PROMPT = [
  "You are advising an airport's chief executive on its capital portfolio from the material given and nothing else.",
  "You may explain, prioritise and argue; you must not state any number that is not in the material.",
  "Say when a figure is the demonstration's or entered rather than a platform record. Be brief and direct.",
].join(" ");

export function materialForCapex(
  d: Pick<CapexDesk, "tiles" | "projects" | "financeLines" | "boardLine" | "currency">,
): string {
  const L: string[] = [];
  for (const t of d.tiles)
    L.push(
      `Tile ${t.label}: ${t.value == null ? "n/a" : Math.round(t.value)} ${t.unit} — ${t.text}${t.assumed ? " [demo/entered]" : ""}`,
    );
  for (const p of d.projects)
    L.push(
      `Project ${p.code} "${p.name}" (${p.status}): budget ${p.budget == null ? "n/a" : Math.round(p.budget)}, committed ${p.committed == null ? "n/a" : Math.round(p.committed)}, spent ${p.spent == null ? "n/a" : Math.round(p.spent)} (${p.spentPct ?? "n/a"}%), calendar ${p.elapsedPct ?? "n/a"}% elapsed, ${p.slipDays} days late, ${p.gates}, ${p.holding} hold points${p.workStopped ? " STOPPED" : ""}. Verdict: ${VERDICT_LABEL[p.verdict]} — ${p.why}.`,
    );
  for (const c of d.financeLines)
    L.push(
      `Finance line ${c.id} "${c.name}": budget ${Math.round(c.budget)}, spent ${Math.round(c.spent)} (${c.pctSpent}%), ${c.pctComplete}% complete, spend ${c.burnAhead} points ahead of progress.`,
    );
  L.push(`Board: ${d.boardLine.join(" ")}`);
  return L.join("\n");
}

export function answerCapex(q: string, d: CapexDesk): string | null {
  const t = q.toLowerCase();
  if (/stopped|hold/.test(t))
    return (
      d.projects
        .filter((p) => p.verdict === "hold")
        .map((p) => `${p.name}: ${p.why}`)
        .join("\n") || "Nothing is stopped at a hold point."
    );
  if (/late|slip|behind|rebaseline/.test(t))
    return (
      d.projects
        .filter((p) => p.slipDays > 0)
        .map((p) => `${p.name}: ${p.slipDays} days late — ${VERDICT_LABEL[p.verdict]}`)
        .join("\n") || "Nothing is behind its baseline."
    );
  if (/spend|burn|ahead|money|budget/.test(t))
    return d.projects
      .filter((p) => p.budget != null)
      .map(
        (p) =>
          `${p.name}: ${money(p.spent ?? 0, d.currency)} of ${money(p.budget ?? 0, d.currency)} (${p.spentPct}%), calendar ${p.elapsedPct ?? "—"}% — ${p.burnAhead != null && p.burnAhead > 0 ? `${p.burnAhead} points ahead` : "within the calendar"}`,
      )
      .join("\n");
  if (/first|priorit|do/.test(t)) return d.boardLine[1];
  if (/board|summar/.test(t)) return d.boardLine.join(" ");
  const p = d.projects.find((x) => t.includes(x.code.toLowerCase()) || t.includes(x.name.toLowerCase().slice(0, 12)));
  if (p)
    return `${p.name} (${p.status}): ${VERDICT_LABEL[p.verdict]} — ${p.why}. ${p.budget != null ? `${money(p.spent ?? 0, d.currency)} of ${money(p.budget, d.currency)}.` : "No money declared."} ${p.gates}.`;
  return null;
}
